// The after-credits film under the footer (web/site/_cinema.html, ported from the concept
// site's cinema.js): the plate is a 3416×1128 painting scaled to the viewport; the tiny
// screen is a video projected onto its quad, the beam flickers with it, dust drifts through
// the light on a canvas, the reels spin (CSS) and the two creatures blink. Runs only while
// on screen and the tab is visible; reduced motion = the still poster.

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

const L = {
  reelBig: { x: 193, y: 293, w: 139, h: 139 },
  reelSmall: { x: 77, y: 342, w: 117, h: 117 },
  beam: { x: 300, y: 230, w: 1800, h: 390, lens: [312, 462] as const },
  screen: {
    quad: [
      [2089, 258],
      [3214, 93],
      [3214, 875],
      [2089, 872],
    ] as [number, number][],
  },
  eyes: {
    blue_l: { x: 592, y: 649, w: 65, h: 72 },
    blue_r: { x: 667, y: 634, w: 60, h: 69 },
    green_l: { x: 1325, y: 694, w: 60, h: 74 },
    green_r: { x: 1386, y: 690, w: 50, h: 71 },
  } as Record<string, Rect>,
};
const SW = 3416;
const SH = 1128;
const ASSETS = "/static/cinema/";

type Mat = [number, number, number, number, number, number, number, number, number];

/** The matrix3d that maps a w×h box onto the quad (a projective transform, as CSS wants it). */
function projection(w: number, h: number, q: [number, number][]): string {
  const at = (m: Mat, i: number) => m[i] ?? 0;
  const adj = (m: Mat): Mat => [
    m[4] * m[8] - m[5] * m[7],
    m[2] * m[7] - m[1] * m[8],
    m[1] * m[5] - m[2] * m[4],
    m[5] * m[6] - m[3] * m[8],
    m[0] * m[8] - m[2] * m[6],
    m[2] * m[3] - m[0] * m[5],
    m[3] * m[7] - m[4] * m[6],
    m[1] * m[6] - m[0] * m[7],
    m[0] * m[4] - m[1] * m[3],
  ];
  const mul = (a: Mat, b: Mat): Mat => {
    const c: Mat = [0, 0, 0, 0, 0, 0, 0, 0, 0];
    for (let i = 0; i < 3; i++) {
      for (let j = 0; j < 3; j++) {
        let s = 0;
        for (let k = 0; k < 3; k++) s += at(a, 3 * i + k) * at(b, 3 * k + j);
        c[3 * i + j] = s;
      }
    }
    return c;
  };
  const basis = (
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    x3: number,
    y3: number,
    x4: number,
    y4: number,
  ): Mat => {
    const m: Mat = [x1, x2, x3, y1, y2, y3, 1, 1, 1];
    const a = adj(m);
    const v = [
      a[0] * x4 + a[1] * y4 + a[2],
      a[3] * x4 + a[4] * y4 + a[5],
      a[6] * x4 + a[7] * y4 + a[8],
    ] as const;
    return mul(m, [v[0], 0, 0, 0, v[1], 0, 0, 0, v[2]]);
  };
  const [q0, q1, q2, q3] = q;
  if (!q0 || !q1 || !q2 || !q3) return "";
  const s = basis(0, 0, w, 0, 0, h, w, h);
  const d = basis(q0[0], q0[1], q1[0], q1[1], q3[0], q3[1], q2[0], q2[1]);
  const t = mul(d, adj(s));
  const n = t.map((v) => v / t[8]);
  return `matrix3d(${n[0]},${n[3]},0,${n[6]},${n[1]},${n[4]},0,${n[7]},0,0,1,0,${n[2]},${n[5]},0,${n[8]})`;
}

export function cinema(): void {
  const footer = document.getElementById("cinemaFooter");
  const stage = document.getElementById("cinemaStage");
  if (!footer || !stage) return;
  const need = (id: string): HTMLElement => {
    const el = document.getElementById(id);
    if (!el) throw new Error(`cinema: no #${id} (web/site/_cinema.html)`);
    return el;
  };
  const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;
  const place = (el: HTMLElement, r: Rect) => {
    el.style.left = `${r.x}px`;
    el.style.top = `${r.y}px`;
    el.style.width = `${r.w}px`;
    el.style.height = `${r.h}px`;
  };
  const reelBig = need("cinemaReelBig");
  place(reelBig, L.reelBig);
  place(need("cinemaReelSmall"), L.reelSmall);
  for (const rim of stage.querySelectorAll<HTMLElement>(".cinema-reel-rim")) {
    place(rim, rim.previousElementSibling === reelBig ? L.reelBig : L.reelSmall);
  }
  const beamDim = need("cinemaBeamDim");
  const beamBoost = need("cinemaBeamBoost");
  const dust = need("cinemaDust") as HTMLCanvasElement;
  const lens = need("cinemaLens");
  const movie = need("cinemaMovie") as HTMLVideoElement;
  place(beamDim, L.beam);
  place(beamBoost, L.beam);
  place(dust, L.beam);
  dust.width = L.beam.w;
  dust.height = L.beam.h;
  lens.style.left = `${L.beam.lens[0] - 110}px`;
  lens.style.top = `${L.beam.lens[1] - 110}px`;
  const eyes: { el: HTMLElement; who: string }[] = [];
  for (const [name, r] of Object.entries(L.eyes)) {
    const eye = document.createElement("div");
    eye.className = "cinema-eye";
    place(eye, r);
    eye.style.maskImage = `url(${ASSETS}eye_${name}_mask.png)`;
    eye.style.setProperty("-webkit-mask-image", eye.style.maskImage);
    const lid = document.createElement("div");
    lid.className = "cinema-lid";
    lid.style.backgroundImage = `url(${ASSETS}eye_${name}_lid.png)`;
    eye.appendChild(lid);
    stage.appendChild(eye);
    eyes.push({ el: eye, who: name.split("_")[0] ?? "" });
  }
  need("cinemaScreen").style.transform = projection(1280, 800, L.screen.quad);

  const fitStage = () => {
    const w = footer.clientWidth;
    const s = Math.min(w / SW, innerHeight / SH);
    const left = (w - SW * s) / 2;
    footer.style.height = `${Math.round(SH * s)}px`;
    stage.style.transform = `translate(${left}px,0) scale(${s})`;
  };
  if ("ResizeObserver" in window) new ResizeObserver(fitStage).observe(footer);
  addEventListener("resize", fitStage, { passive: true });
  fitStage();

  const ctx = dust.getContext("2d");
  const motes = Array.from({ length: 70 }, () => ({
    x: Math.random(),
    y: Math.random(),
    r: 1.2 + Math.random() * 2.2,
    vx: 0.00035 + Math.random() * 0.0007,
    ph: Math.random() * 6.28,
    sp: 0.4 + Math.random() * 0.8,
  }));
  let slow = 0;
  let drop = 0;
  let running = false;
  let raf = 0;
  let last = 0;
  const frame = (ts: number) => {
    if (!running) return;
    const dt = Math.min(50, ts - last || 16);
    last = ts;
    slow += (Math.random() - 0.5) * 0.08;
    slow *= 0.96;
    if (drop <= 0 && Math.random() < 0.006) drop = 2 + Math.random() * 4;
    drop -= dt / 16;
    let f = slow + (Math.random() - 0.5) * 0.55 - (drop > 0 ? 0.9 : 0);
    f = Math.max(-1, Math.min(1, f));
    beamBoost.style.opacity = String(Math.max(0, f) * 0.55);
    beamDim.style.opacity = String(Math.max(0, -f) * 0.5);
    lens.style.opacity = String(0.35 + f * 0.35);
    movie.style.filter = `brightness(${(1 + f * 0.12).toFixed(3)})`;
    if (ctx) {
      ctx.clearRect(0, 0, dust.width, dust.height);
      const bright = 0.25 + Math.max(0, f) * 0.6 - Math.max(0, -f) * 0.2;
      for (const m of motes) {
        m.x += m.vx * dt * 0.06 * m.sp;
        m.ph += dt * 0.0012 * m.sp;
        if (m.x > 1.02) {
          m.x = -0.02;
          m.y = Math.random();
        }
        const top = 208 + (20 - 208) * m.x;
        const bot = 258 + (370 - 258) * m.x;
        const y = top + (bot - top) * m.y + Math.sin(m.ph) * 6;
        const x = m.x * dust.width;
        ctx.beginPath();
        ctx.arc(x, y, m.r, 0, 6.28);
        ctx.fillStyle = `rgba(255,222,170,${(bright * (0.5 + 0.5 * Math.sin(m.ph * 1.7))).toFixed(3)})`;
        ctx.fill();
      }
    }
    raf = requestAnimationFrame(frame);
  };
  const timers: number[] = [];
  const blink = (who: string) => {
    const set = eyes.filter((e) => e.who === who);
    const close = () => {
      for (const e of set) e.el.classList.add("closing");
    };
    const open = () => {
      for (const e of set) e.el.classList.remove("closing");
    };
    close();
    timers.push(window.setTimeout(open, 130));
    if (Math.random() < 0.22) {
      timers.push(window.setTimeout(close, 300));
      timers.push(window.setTimeout(open, 430));
    }
  };
  const schedule = (who: string, first = false) => {
    const wait = first ? 800 + Math.random() * 2500 : 2400 + Math.random() * 4800;
    timers.push(
      window.setTimeout(() => {
        if (!running) return;
        blink(who);
        schedule(who);
      }, wait),
    );
  };
  const start = () => {
    if (running || reduced) {
      if (reduced) movie.pause();
      return;
    }
    running = true;
    footer.classList.add("running");
    last = 0;
    raf = requestAnimationFrame(frame);
    schedule("blue", true);
    schedule("green", true);
    movie.play().catch(() => {});
  };
  const stop = () => {
    running = false;
    footer.classList.remove("running");
    cancelAnimationFrame(raf);
    for (const t of timers.splice(0)) clearTimeout(t);
    movie.pause();
  };
  let inView = false;
  new IntersectionObserver(
    ([entry]) => {
      inView = entry?.isIntersecting ?? false;
      if (inView) start();
      else stop();
    },
    { threshold: 0.05 },
  ).observe(footer);
  document.addEventListener("visibilitychange", () => {
    if (document.hidden || !inView) stop();
    else start();
  });
}
